import React, { useMemo, useState } from "react";
import "../styles/PopEvent.css";
import { FaArrowLeft, FaEdit, FaTrash, FaClock } from "react-icons/fa";
import { toYMD } from "../utils/recurringUtils";

const PopEvent = ({ event, onClose, onDelete, onEdit }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState(null); // "edit" | "delete"

  const safeEvent = event || {
    id: "",
    title: "",
    start: null,
    extendedProps: {},
  };

  const ext = safeEvent.extendedProps || {};
  const {
    isKuliah = false,
    speaker = "",
    speakerImage = "",
    speakerPosition = "",
    subjek = "",
    tajuk = "",
    time = "",
    nota = "",
    isRecurring = false,

    kitabId = null,
    kitabTitle = "",
    kitabCoverImage = "",
    kitabAuthor = "",
    kitabCategory = "",
  } = ext;

  const hasKitab = !!(kitabId || kitabTitle);

  const seriesId =
    ext.seriesId ||
    safeEvent.groupId ||
    (String(safeEvent.id || "").includes("__")
      ? String(safeEvent.id).split("__")[0]
      : safeEvent.id);

  const startDate = safeEvent.start || safeEvent._instance?.range?.start || null;
  const occurrenceYMD = startDate ? toYMD(startDate) : null;

  const imageSrc = isKuliah ? speakerImage : ext.posterImage;
  const imgWidth = isKuliah ? "100px" : "140px";
  const borderRadiusStyle = isKuliah ? "50%" : "0";
  const borderStyle = "4px solid white";

  const formattedTime = useMemo(() => {
    if (!time) return "";
    let [hour, minute] = String(time).split(":");
    hour = parseInt(hour, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour}:${minute} ${ampm}`;
  }, [time]);

  const formattedDate = useMemo(() => {
    if (!startDate) return "";
    const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
    return new Date(startDate).toLocaleDateString("ms-MY", options);
  }, [startDate]);

  if (!event) return null;

  const openConfirm = (mode) => {
    setConfirmMode(mode);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmMode(null);
  };

  const runAction = (scope) => {
    // scope: "single" | "one" | "series"
    if (confirmMode === "edit") {
      onEdit?.({ scope, eventId: seriesId, occurrenceYMD });
    } else if (confirmMode === "delete") {
      onDelete?.({ scope, eventId: seriesId, occurrenceYMD });
    }
    closeConfirm();
  };

  const handleEditClick = () => {
    if (!isRecurring) {
      onEdit?.({ scope: "single", eventId: event.id, occurrenceYMD });
      return;
    }
    openConfirm("edit");
  };

  const handleDeleteClick = () => {
    if (!isRecurring) {
      onDelete?.({ scope: "single", eventId: event.id, occurrenceYMD });
      return;
    }
    openConfirm("delete");
  };

  return (
    <div className="pop-event-container">
      <h2 className="pop-event-title">{event.title}</h2>
      <div className="pop-event-date">{formattedDate}</div>

      <div className="pop-event-image">
        <img
          src={imageSrc}
          alt={isKuliah ? "Speaker" : "Poster"}
          style={{
            width: imgWidth,
            height: "100px",
            borderRadius: borderRadiusStyle,
            objectFit: "cover",
            border: borderStyle,
          }}
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      </div>

      <div className="pop-event-content">
        <h4 className="pop-event-speaker-name">{isKuliah ? speaker : ""}</h4>
        <p className="pop-event-speaker-position">{isKuliah ? speakerPosition : ""}</p>
      </div>

      {isKuliah && (
        <div className="kitab-container">
          {hasKitab ? (
            <div className="kitab-row">
              {kitabCoverImage ? (
                <img
                  src={kitabCoverImage}
                  alt="Kitab"
                  className="kitab-cover"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              ) : null}

              <div className="kitab-meta">
                <p className="kitab-title">{kitabTitle || tajuk || ""}</p>
                <p className="kitab-sub">
                  {(kitabCategory || subjek || "")}
                  {kitabAuthor ? ` • ${kitabAuthor}` : ""}
                </p>
              </div>
            </div>
          ) : (
            <>
              <p className="pop-event-subject">{subjek || ""}</p>
              <p className="pop-event-tajuk">{tajuk || ""}</p>
            </>
          )}
        </div>
      )}

      <div className="waktu-container">
        <FaClock />
        <p className="pop-event-time">{formattedTime || ""}</p>
      </div>

      {isKuliah && nota && (
        <div className="nota-container">
          <p className="pop-event-nota">{nota}</p>
        </div>
      )}

      <div className="pop-event-buttons">
        <button className="pop-event-button" onClick={onClose} title="Kembali" type="button">
          <FaArrowLeft className="icon" />
        </button>

        <button className="pop-event-button" onClick={handleEditClick} title="Edit" type="button">
          <FaEdit className="icon" />
        </button>

        <button className="pop-event-button" onClick={handleDeleteClick} title="Padam" type="button">
          <FaTrash className="icon" />
        </button>
      </div>

      {/* ✅ Confirm panel (recurring only) */}
      {confirmOpen && (
        <div className="popevent-confirm-overlay">
          <div className="popevent-confirm-box">
            <h4 style={{ margin: 0 }}>
              {confirmMode === "edit" ? "Kemaskini aktiviti" : "Padam aktiviti"}
            </h4>

            <p style={{ marginTop: 8, marginBottom: 14, opacity: 0.9 }}>
              Pilih tindakan untuk siri berulang ini:
            </p>

            <div className="popevent-confirm-actions">
              {confirmMode === "edit" ? (
                <>
                  <button
                    type="button"
                    className="popevent-confirm-btn"
                    onClick={() => runAction("one")}
                  >
                    Tarikh ini sahaja
                  </button>

                  <button
                    type="button"
                    className="popevent-confirm-btn danger"
                    onClick={() => runAction("series")}
                  >
                    Semua siri
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="popevent-confirm-btn"
                    onClick={() => runAction("one")}
                  >
                    Tarikh ini sahaja
                  </button>

                  <button
                    type="button"
                    className="popevent-confirm-btn danger"
                    onClick={() => runAction("series")}
                  >
                    Semua siri
                  </button>
                </>
              )}

              <button
                type="button"
                className="popevent-confirm-btn secondary"
                onClick={closeConfirm}
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopEvent;
